import { Controller } from '@hotwired/stimulus'

const SETTLE_MS = 150
const TYPE_MS_PER_CHAR = 16
const PAUSE_MS = 380

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

// Plays the thread once it scrolls into view: each row shows its avatar
// and typing dots, then its bubble types out character by character.
export default class extends Controller {
  static targets = ['row']

  connect() {
    this.cancelled = false
    this.messages = this.rowTargets.map(row => this.#parse(row))

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    this.messages.forEach(({ avatar, bubble, text }) => {
      avatar.hidden = true
      bubble.hidden = true
      text.textContent = ''
    })

    this.observer = new IntersectionObserver(
      this.#handleIntersect.bind(this), { threshold: 0.4 }
    )
    this.observer.observe(this.element)
  }

  disconnect() {
    this.cancelled = true
    this.observer?.disconnect()
  }

  #parse(row) {
    const text = row.querySelector('message-text')

    return {
      avatar: row.querySelector('user-avatar'),
      bubble: row.querySelector('message-bubble'),
      caret: row.querySelector('type-caret'),
      copy: text.textContent.replace(/\s+/g, ' ').trim(),
      text,
      typing: row.querySelector('typing-indicator'),
      typingDelay: Number(row.dataset.typingDelay)
    }
  }

  #handleIntersect(entries) {
    if (!entries.some(entry => entry.isIntersecting)) return

    this.observer.disconnect()
    this.#play()
  }

  async #play() {
    await sleep(SETTLE_MS)

    for (const message of this.messages) {
      if (this.cancelled) return

      message.avatar.hidden = false
      message.typing.hidden = false
      await sleep(message.typingDelay)
      if (this.cancelled) return

      message.typing.hidden = true
      message.bubble.hidden = false
      message.caret.hidden = false

      for (let length = 1; length <= message.copy.length; length++) {
        if (this.cancelled) return

        message.text.textContent = message.copy.slice(0, length)
        await sleep(TYPE_MS_PER_CHAR)
      }

      message.caret.hidden = true
      await sleep(PAUSE_MS)
    }
  }
}
